// src/components/auth/RequireAuth.tsx
// Route protection component that redirects unauthenticated users to login
//
// CRITICAL: Does NOT redirect during boot transitions (loading, workspace_closed, unavailable).
// During these states, BootGate owns the display (spinner or overlay).
// Redirecting to /login during a workspace switch would undo the switch.

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAppSelector } from '../../store/store';
import { selectBootStatus, selectIsBootReady } from '../../store/slices/bootContextSlice';
import { AUTH_MODE } from '../../config/centralAuth';

interface RequireAuthProps {
  children: React.ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, authSource, user, token } = useAuth();
  const location = useLocation();
  const bootStatus = useAppSelector(selectBootStatus);
  // H-2C2: in the OIDC RP (BFF) model the browser holds no Bearer token —
  // only the opaque `crm_sid` HttpOnly cookie. `RpBootGate` proves auth by
  // calling the server-only `GET /auth/context` and dispatching `setBootReady`
  // on success, so `bootStatus === 'ready'` IS the authenticated signal for
  // the RP path. `useAuth().isAuthenticated` only knows the legacy / central
  // Bearer paths; recognize RP boot-ready as a third valid auth source here.
  // Scoped to `RequireAuth` to keep the legacy `useAuth` contract unchanged.
  const isBootReady = useAppSelector(selectIsBootReady);
  const isAuthenticatedEffective = isAuthenticated || isBootReady;

  // DEV ONLY: Log auth check
  if (import.meta.env.DEV) {
    console.log('[RequireAuth] Auth check:', {
      isAuthenticated,
      isBootReady,
      authSource,
      hasUser: !!user,
      hasToken: !!token,
      userId: user?.id,
      pathname: location.pathname,
      bootStatus,
    });
  }

  if (!isAuthenticatedEffective) {
    // CRITICAL: Do NOT redirect to /login during Central boot transitions.
    // During workspace switch, bootStatus is 'loading'. BootGate shows a spinner.
    // If we redirect here, we undo the workspace switch and create a loop:
    //   switch → 401 burst → logout → /login → /dashboard (old workspace)
    //
    // Only redirect when:
    //   - Legacy mode (no boot gate), OR
    //   - Boot is ready (normal unauthenticated state), OR
    //   - Boot is 'unauthenticated' (Central explicitly said "not authenticated")
    //   - Boot is idle with no token (initial unauthenticated state)
    const isCentralMode = AUTH_MODE !== 'legacy';
    const isBootTransitioning = isCentralMode &&
      (bootStatus === 'loading' || bootStatus === 'workspace_closed' || bootStatus === 'unavailable');

    if (isBootTransitioning) {
      // Boot is in a transition state — BootGate handles UI.
      // Do NOT redirect. Render nothing (BootGate's spinner/overlay is above us).
      return null;
    }

    // Safe to redirect — boot is ready/idle/unauthenticated, and user is not authenticated
    console.warn('[RequireAuth] Not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export default RequireAuth;
